import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, downloadFile, errMsg, MODULES } from "@/lib/api";
import { money, usd } from "@/lib/currency";
import { PageHero } from "@/pages/site/ContentPage";
import { ChangePassword } from "@/components/ChangePassword";
import { DigestToggle } from "@/components/site/DigestToggle";

const label = (id) => MODULES.find((m) => m.id === id)?.label || id;

export default function Account() {
  const { data } = useQuery({ queryKey: ["account"], queryFn: async () => (await api.get("/account")).data });
  if (!data) return <div className="py-24 text-center font-mono text-xs text-slate-500">LOADING…</div>;
  const active = (s) => s.status === "active" && s.end_date > new Date().toISOString();
  return (
    <div>
      <PageHero eyebrow="My Account" title={data.user.name} subtitle={`${data.user.email}${data.user.company ? " · " + data.user.company : ""}`} />
      <div className="max-w-[1100px] mx-auto px-4 md:px-8 py-12 space-y-10">
        <section data-testid="account-subscriptions">
          <div className="flex items-center justify-between mb-4"><h2 className="text-xl font-bold">Dashboard subscriptions</h2><Link to="/subscribe" className="text-sm text-[#F58B23]" data-testid="account-add-subscription">+ Add subscription</Link></div>
          {data.subscriptions.length ? (
            <div className="grid md:grid-cols-2 gap-3">
              {data.subscriptions.map((s) => (
                <div key={s.id} className="bg-[#0F172A] border border-slate-800 rounded-lg p-5">
                  <div className="flex justify-between"><p className="font-semibold">{s.product === "*" ? "All products" : s.product}</p>
                    <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${active(s) ? "bg-[#03AB9E]/15 text-[#03AB9E]" : "bg-slate-800 text-slate-400"}`}>{active(s) ? "Active" : "Expired"}</span></div>
                  <p className="text-sm text-slate-400 mt-1">{label(s.module)} · {s.regions.join(", ")}</p>
                  <div className="flex items-center justify-between mt-2"><p className="text-xs font-mono text-slate-500">Valid till {s.end_date.slice(0, 10)}</p>
                    {active(s) && <Link to={MODULES.find((m) => m.id === s.module)?.path || "/dashboards"} data-testid={`account-open-dashboard-${s.id}`} className="text-xs px-3 py-1.5 rounded bg-[#1074BA] hover:bg-[#0D629E] text-white">Open dashboard</Link>}</div>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-400">No subscriptions yet. <Link to="/subscribe" className="text-[#03AB9E]">Subscribe to a dashboard</Link>.</p>}
        </section>
        <section data-testid="account-reports">
          <h2 className="text-xl font-bold mb-4">My reports</h2>
          {data.purchases.length ? data.purchases.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-4 border-b border-slate-800 py-3">
              <Link to={`/reports/${p.report_id}`} className="text-sm hover:text-[#03AB9E]">{p.title}</Link>
              <button disabled={!p.available} onClick={() => downloadFile(`/reports/${p.report_id}/download`, "report.pdf").catch((e) => toast.error(errMsg(e)))}
                className="text-xs px-3 py-1.5 rounded border border-slate-700 hover:border-[#03AB9E] disabled:opacity-40 shrink-0">{p.available ? "Download" : "Preparing"}</button>
            </div>
          )) : <p className="text-sm text-slate-400">No reports purchased yet.</p>}
        </section>
        <section>
          <h2 className="text-xl font-bold mb-4">Orders</h2>
          {data.orders.map((o) => (
            <div key={o.id} data-testid={`account-order-${o.id}`} className="flex justify-between text-sm border-b border-slate-800 py-2.5"><span className="text-slate-300 truncate pr-4">{o.description}</span>
              <div className="font-mono shrink-0">{o.currency && o.currency !== "USD" ? `${money(o.amount_local, o.currency)} (${usd(o.amount_usd)})` : usd(o.amount_usd ?? o.amount_inr)} · <span className={o.status === "paid" ? "text-[#03AB9E]" : "text-slate-500"}>{o.status}</span>
              {o.status === "paid" && <button onClick={() => downloadFile(`/orders/${o.id}/invoice`, "invoice.pdf").catch((e) => toast.error(errMsg(e)))} data-testid={`account-invoice-${o.id}`} className="ml-3 text-xs px-2 py-1 rounded border border-slate-700 hover:border-[#03AB9E]">Invoice</button>}</div></div>
          ))}
        </section>
        <section id="security"><h2 className="text-xl font-bold mb-4">Email & security</h2><div className="space-y-4"><DigestToggle /><ChangePassword /></div></section>
      </div>
    </div>
  );
}
